export type ReadinessLevel = 'HIGH' | 'MEDIUM' | 'LOW';
export type RiskLevel     = 'GREEN' | 'YELLOW' | 'RED';
export type DTLevel       = 1 | 2 | 3 | 4 | 5 | 6;
export type RouteOutcome  =
    | 'self-service'
    | 'peer-to-peer'
    | 'ccta-support'
    | 'community-expert'
    | 'academic-expert'
    | 'crisis';
export type IssueStatus   = 'OPEN' | 'IN_PROGRESS' | 'RESOLVED';

export type PlatformIssueCategory = 'System Bug' | 'Data Issue' | 'UI Problem' | 'Feature Request' | 'Performance Issue';
export type ReporterType = 'site-user' | 'internal';

export interface PlatformIssue {
    id:             string;
    title:          string;
    description:    string;
    urgency:        'HIGH' | 'MEDIUM' | 'LOW';
    issueType:      PlatformIssueCategory;
    reporterName:   string;
    reporterType:   ReporterType;
    siteName?:      string;
    siteRole?:      string;
    submittedAt:    string;
    status:         IssueStatus;
    resolution?:    string;
    resolvedAt?:    string;
}

export interface CARIScores {
    commitment:      number;
    adaptability:    number;
    resources:       number;
    infrastructure:  number;
}

export interface HELIOSScores {
    acceptability:      number;
    adoption:           number;
    appropriateness:    number;
    feasibility:        number;
    fidelity:           number;
    implementationCost: number;
    coverage:           number;
    sustainability:     number;
    scalability:        number;
}

export interface SiteAdaptation {
    date:        string;
    domain:      string;
    description: string;
    outcome:     'POSITIVE' | 'NEUTRAL' | 'PENDING';
}

export interface Issue {
    id:             string;
    siteId:         string;
    title:          string;
    description:    string;
    urgency:        'HIGH' | 'MEDIUM' | 'LOW';
    issueType:      string;
    dtLevel:        DTLevel;
    route:          RouteOutcome;
    recommendation: string;
    taProvider?:    string;
    submittedAt:    string;
    resolvedAt?:    string;
    resolution?:    string;
    status:         IssueStatus;
}

export interface Site {
    id:               string;
    name:             string;
    city:             string;
    state:            string;
    siteType:         string;
    contactName:      string;
    contactEmail:     string;
    patientVolume:    number;
    interventionType: string;
    interventionName: string;
    /**
     * Canonical intervention key (backend intervention_profiles.intervention_id),
     * threaded from the authenticated session (task #402). Optional — legacy
     * sites without a configured intervention fall back to the synthetic
     * `intervention-${site.id}` placeholder at the portal mount point.
     */
    interventionId?: string;
    enrollmentCount:  number;
    readiness:        ReadinessLevel;
    cariScores:       CARIScores;
    heliosScores:     HELIOSScores;
    adaptations:      SiteAdaptation[];
    rfScore:          number;
    heliosRisk:       RiskLevel;
    dtLevel:          DTLevel;
    viralSuppression: number;
    retention:        number;
    pendingRequests:  number;
    lastCheckIn:      string;
    issues:           Issue[];
}

const LEVEL_WEIGHTS: Record<number, number> = {
    1: 0.4, 2: 1.1, 3: 1.9, 4: 2.5, 5: 4.0, 6: 6.0,
};

export function computeTAWeight(site: Site): number {
    return site.issues.reduce((sum, iss) => sum + (LEVEL_WEIGHTS[iss.dtLevel] ?? 1.0), 0);
}

export function cariAvg(scores: CARIScores): number {
    const vals = Object.values(scores);
    return vals.reduce((a, b) => a + b, 0) / vals.length;
}

export const SITES: Site[] = [
    {
        id: 'site-chi-001',
        name: 'Community Health Network',
        city: 'Chicago', state: 'IL',
        siteType: 'FQHC',
        contactName: 'Dr. Renee Okafor',
        contactEmail: 'r.okafor@chn-chi.org',
        patientVolume: 1240,
        interventionType: 'HIV',
        interventionName: 'TXTXT — Text Messaging for ART Adherence (E2i)',
        enrollmentCount: 148,
        readiness: 'HIGH',
        cariScores: { commitment: 0.88, adaptability: 0.82, resources: 0.76, infrastructure: 0.84 },
        heliosScores: {
            acceptability:      8.5,
            adoption:           8.2,
            appropriateness:    8.7,
            feasibility:        8.1,
            fidelity:           7.9,
            implementationCost: 7.2,
            coverage:           8.0,
            sustainability:     8.3,
            scalability:        7.8,
        },
        adaptations: [
            { date: '2026-01-15', domain: 'Workforce', description: 'Introduced peer-buddy onboarding for new case managers to reduce ramp time from 6 to 3 weeks.', outcome: 'POSITIVE' },
            { date: '2026-02-20', domain: 'Data Systems', description: 'Shifted EHR data entry to end-of-shift batching to reduce real-time burden on clinical staff.', outcome: 'POSITIVE' },
        ],
        rfScore: 0.83,
        heliosRisk: 'GREEN',
        dtLevel: 2,
        viralSuppression: 0.89,
        retention: 0.91,
        pendingRequests: 1,
        lastCheckIn: '2026-03-10',
        issues: [
            {
                id: 'iss-001', siteId: 'site-chi-001',
                title: 'Staff turnover affecting case management',
                description: 'Lost 2 case managers this quarter; patient follow-up gaps emerging.',
                urgency: 'MEDIUM', issueType: 'Workforce',
                dtLevel: 2, route: 'peer-to-peer',
                recommendation: 'Connect with a peer site that resolved similar turnover. Peer learning session + shared cross-training templates.',
                taProvider: 'Marcus Williams',
                submittedAt: '2026-03-08', status: 'IN_PROGRESS',
            },
        ],
    },
    {
        id: 'site-hou-002',
        name: 'Hope Clinic',
        city: 'Houston', state: 'TX',
        siteType: 'Community Clinic',
        contactName: 'James Tran',
        contactEmail: 'jtran@hopeclinic-hou.org',
        patientVolume: 680,
        interventionType: 'HIV',
        interventionName: 'SBIRT — Substance Use Screening & Brief Intervention (E2i)',
        enrollmentCount: 67,
        readiness: 'MEDIUM',
        cariScores: { commitment: 0.71, adaptability: 0.64, resources: 0.58, infrastructure: 0.62 },
        heliosScores: {
            acceptability:      6.8,
            adoption:           6.2,
            appropriateness:    7.1,
            feasibility:        6.4,
            fidelity:           5.9,
            implementationCost: 5.8,
            coverage:           6.5,
            sustainability:     6.0,
            scalability:        5.5,
        },
        adaptations: [
            { date: '2025-11-10', domain: 'Patient Engagement', description: 'Piloted bilingual appointment reminder texts (English/Spanish); no-show rate dropped 8% in first month.', outcome: 'POSITIVE' },
            { date: '2026-01-22', domain: 'Data & Reporting', description: 'Assigned a part-time data coordinator to clear EHR backlog; scope still being assessed.', outcome: 'PENDING' },
        ],
        rfScore: 0.64,
        heliosRisk: 'YELLOW',
        dtLevel: 3,
        viralSuppression: 0.73,
        retention: 0.76,
        pendingRequests: 3,
        lastCheckIn: '2026-03-05',
        issues: [
            {
                id: 'iss-002', siteId: 'site-hou-002',
                title: 'Low viral suppression — missed Q1 targets',
                description: 'Viral suppression dropped from 79% to 73%. Appointment no-shows up 18%.',
                urgency: 'HIGH', issueType: 'Clinical Quality',
                dtLevel: 4, route: 'community-expert',
                recommendation: 'DT Level 4 Community Expert engaged. Intensive adherence protocol, PDSA cycle, and patient retention review.',
                taProvider: 'Dr. Aisha Patel',
                submittedAt: '2026-03-06', status: 'OPEN',
            },
            {
                id: 'iss-003', siteId: 'site-hou-002',
                title: 'EHR data entry backlog',
                description: 'Patient records 3–4 weeks behind; affecting reporting accuracy.',
                urgency: 'MEDIUM', issueType: 'Data & Reporting',
                dtLevel: 2, route: 'peer-to-peer',
                recommendation: 'Connect with Community Health Network for EHR workflow best practices.',
                submittedAt: '2026-03-07', status: 'OPEN',
            },
            {
                id: 'iss-005', siteId: 'site-hou-002',
                title: 'Appointment reminder system overhaul',
                description: 'Current phone-only reminder system insufficient. 22% no-contact rate prior to visits.',
                urgency: 'LOW', issueType: 'Patient Engagement',
                dtLevel: 1, route: 'self-service',
                recommendation: 'Review TERRA resource library for patient engagement technology options.',
                submittedAt: '2026-02-14',
                resolvedAt: '2026-02-28',
                resolution: 'Implemented bilingual SMS reminder pilot via TERRA self-service toolkit. No-show rate improved 8%.',
                status: 'RESOLVED',
            },
        ],
    },
    {
        id: 'site-oak-003',
        name: 'Pacific Care Center',
        city: 'Oakland', state: 'CA',
        siteType: 'Health Center',
        contactName: 'Dr. Maya Rodríguez',
        contactEmail: 'm.rodriguez@pacificcare-oak.org',
        patientVolume: 920,
        interventionType: 'HIV',
        interventionName: 'Project CONNECT — Linkage to HIV Care (E2i)',
        enrollmentCount: 193,
        readiness: 'HIGH',
        cariScores: { commitment: 0.91, adaptability: 0.87, resources: 0.83, infrastructure: 0.89 },
        heliosScores: {
            acceptability:      9.1,
            adoption:           8.8,
            appropriateness:    9.2,
            feasibility:        8.9,
            fidelity:           8.7,
            implementationCost: 8.0,
            coverage:           9.0,
            sustainability:     9.1,
            scalability:        8.4,
        },
        adaptations: [
            { date: '2025-09-05', domain: 'Fidelity', description: 'Introduced monthly fidelity check-ins led by internal champion; protocol adherence improved to 94%.', outcome: 'POSITIVE' },
            { date: '2025-12-01', domain: 'Sustainability', description: 'Secured 2-year bridge funding from local foundation after TERRA sustainability planning session.', outcome: 'POSITIVE' },
            { date: '2026-02-10', domain: 'Scalability', description: 'Began documenting scale-up protocol to prepare for potential expansion to 2 satellite sites.', outcome: 'PENDING' },
        ],
        rfScore: 0.88,
        heliosRisk: 'GREEN',
        dtLevel: 1,
        viralSuppression: 0.93,
        retention: 0.95,
        pendingRequests: 0,
        lastCheckIn: '2026-03-12',
        issues: [],
    },
    {
        id: 'site-phx-004',
        name: 'Sunbelt Health Partners',
        city: 'Phoenix', state: 'AZ',
        siteType: 'Ryan White Clinic',
        contactName: 'Carlos Mendez',
        contactEmail: 'cmendez@sunbelt-phx.org',
        patientVolume: 450,
        interventionType: 'HIV',
        interventionName: 'CoCM — Collaborative Care Management (E2i)',
        enrollmentCount: 38,
        readiness: 'LOW',
        cariScores: { commitment: 0.52, adaptability: 0.44, resources: 0.38, infrastructure: 0.41 },
        heliosScores: {
            acceptability:      4.2,
            adoption:           3.8,
            appropriateness:    5.1,
            feasibility:        3.5,
            fidelity:           3.9,
            implementationCost: 4.5,
            coverage:           3.2,
            sustainability:     2.8,
            scalability:        2.5,
        },
        adaptations: [
            { date: '2025-10-18', domain: 'Resources', description: 'Attempted grant application for supplemental staffing; outcome still pending with state agency.', outcome: 'PENDING' },
        ],
        rfScore: 0.44,
        heliosRisk: 'RED',
        dtLevel: 6,
        viralSuppression: 0.61,
        retention: 0.64,
        pendingRequests: 4,
        lastCheckIn: '2026-02-28',
        issues: [
            {
                id: 'iss-004', siteId: 'site-phx-004',
                title: 'Critical: Funding gap threatening program continuity',
                description: 'Ryan White Part B funding delayed. May not be able to maintain staffing through Q2.',
                urgency: 'HIGH', issueType: 'Funding & Sustainability',
                dtLevel: 6, route: 'crisis',
                recommendation: 'Crisis Intervention activated. Escalate to program officer immediately. Emergency bridge funding protocol and rapid-response implementation support dispatched.',
                taProvider: 'Dr. Sarah Chen',
                submittedAt: '2026-03-01', status: 'IN_PROGRESS',
            },
        ],
    },
];

export const PLATFORM_ISSUES: PlatformIssue[] = [
    {
        id: 'pi-001',
        title: 'Dashboard charts not rendering on Safari',
        description: 'The CARI Intelligence radar charts and bar charts fail to render on Safari 17.x. Users see a blank white area where charts should appear. Works fine on Chrome and Firefox.',
        urgency: 'HIGH',
        issueType: 'System Bug',
        reporterName: 'Marcus Williams',
        reporterType: 'internal',
        submittedAt: '2026-03-18',
        status: 'OPEN',
    },
    {
        id: 'pi-002',
        title: 'Bulk upload CSV parser rejects valid files',
        description: 'When uploading a CSV with special characters in site names (e.g., accented letters), the parser throws an "invalid character" error and rejects the entire file.',
        urgency: 'MEDIUM',
        issueType: 'System Bug',
        reporterName: 'Dr. Sarah Chen',
        reporterType: 'internal',
        submittedAt: '2026-03-15',
        status: 'IN_PROGRESS',
        resolution: 'Investigating encoding issue — likely UTF-8 BOM handling.',
    },
    {
        id: 'pi-003',
        title: 'BIOME scores showing stale data after edit',
        description: 'After updating BIOME scores through the assessment form, the portfolio dashboard still shows the old values until a full page refresh.',
        urgency: 'MEDIUM',
        issueType: 'Data Issue',
        reporterName: 'Dr. Aisha Patel',
        reporterType: 'internal',
        submittedAt: '2026-03-12',
        status: 'OPEN',
    },
    {
        id: 'pi-004',
        title: 'Site portal login page text overlaps on mobile',
        description: 'On screens smaller than 375px wide, the login form labels overlap with the input fields, making it impossible to read or type.',
        urgency: 'LOW',
        issueType: 'UI Problem',
        reporterName: 'James Tran',
        reporterType: 'site-user',
        siteName: 'Hope Clinic',
        siteRole: 'Site Director / Admin',
        submittedAt: '2026-03-10',
        status: 'RESOLVED',
        resolvedAt: '2026-03-14',
        resolution: 'Fixed responsive breakpoints for the login form. Added min-width constraint and adjusted label positioning.',
    },
    {
        id: 'pi-005',
        title: 'Add ability to export issue reports as PDF',
        description: 'It would be very helpful to export a site\'s issue history as a PDF for grant reporting and stakeholder meetings. Currently we have to screenshot each issue individually.',
        urgency: 'LOW',
        issueType: 'Feature Request',
        reporterName: 'Dr. Renee Okafor',
        reporterType: 'site-user',
        siteName: 'Community Health Network',
        siteRole: 'Clinical Director',
        submittedAt: '2026-03-08',
        status: 'OPEN',
    },
    {
        id: 'pi-006',
        title: 'Simulation engine takes 30+ seconds to load',
        description: 'The simulation engine page takes over 30 seconds to become interactive after clicking the nav item. Other pages load in under 2 seconds.',
        urgency: 'HIGH',
        issueType: 'Performance Issue',
        reporterName: 'Carlos Mendez',
        reporterType: 'site-user',
        siteName: 'Sunbelt Health Partners',
        siteRole: 'Program Director',
        submittedAt: '2026-03-20',
        status: 'IN_PROGRESS',
        resolution: 'Profiling initial load — appears related to large dataset hydration.',
    },
    {
        id: 'pi-007',
        title: 'Five Ds chart tooltip shows wrong metric label',
        description: 'When hovering over the "Dissemination" bar in the Five Ds dashboard, the tooltip label says "Discovery" instead. The value appears correct but the label is swapped.',
        urgency: 'MEDIUM',
        issueType: 'UI Problem',
        reporterName: 'Marcus Williams',
        reporterType: 'internal',
        submittedAt: '2026-03-22',
        status: 'OPEN',
    },
    {
        id: 'pi-008',
        title: 'CARI scores rounding inconsistently',
        description: 'Some CARI scores show 2 decimal places while others show 3. The portfolio overview rounds to 2 but the detail view shows 3, causing confusion during reviews.',
        urgency: 'LOW',
        issueType: 'Data Issue',
        reporterName: 'Dr. Maya Rodríguez',
        reporterType: 'site-user',
        siteName: 'Pacific Care Center',
        siteRole: 'Clinician / Provider',
        submittedAt: '2026-03-05',
        status: 'RESOLVED',
        resolvedAt: '2026-03-09',
        resolution: 'Standardized all CARI score displays to 2 decimal places across the application.',
    },
];

export function routeIssue(issueType: string, urgency: string): {
    dtLevel: DTLevel; route: RouteOutcome; recommendation: string;
} {
    const high = urgency === 'HIGH';
    const med  = urgency === 'MEDIUM';

    if (issueType === 'Funding & Sustainability' && high)
        return { dtLevel: 6, route: 'crisis',           recommendation: 'Crisis Intervention activated. Escalate to program officer immediately. Emergency bridge funding protocols initiated.' };
    if (issueType === 'Funding & Sustainability' && med)
        return { dtLevel: 4, route: 'community-expert', recommendation: 'Community Expert engaged for sustainability planning and funding diversification strategy.' };
    if (issueType === 'Clinical Quality' && high)
        return { dtLevel: 4, route: 'community-expert', recommendation: 'Community Expert assigned. Intensive PDSA cycle, adherence protocol, and clinical outcomes review.' };
    if (issueType === 'Clinical Quality' && med)
        return { dtLevel: 3, route: 'ccta-support',     recommendation: 'CCTA-Level specialist consultation scheduled. Quality improvement and outcome measurement review.' };
    if (issueType === 'Enrollment / Linkage to Care' && high)
        return { dtLevel: 4, route: 'community-expert', recommendation: 'Community Expert assigned. Enrollment gaps at this severity require intensive linkage-to-care strategy, barrier analysis, and patient navigation protocol review.' };
    if (issueType === 'Enrollment / Linkage to Care' && med)
        return { dtLevel: 3, route: 'ccta-support',     recommendation: 'CCTA consultation to review linkage-to-care protocols, navigation workflows, and enrollment barriers. Project CONNECT implementation guidance will be referenced.' };
    if (issueType === 'Enrollment / Linkage to Care')
        return { dtLevel: 2, route: 'peer-to-peer',     recommendation: 'Connect with a peer site running a strong linkage-to-care program. Shared navigation templates and outreach checklists available.' };
    if (issueType === 'Viral Suppression — Not Achieving Target' && high)
        return { dtLevel: 4, route: 'community-expert', recommendation: 'Community Expert assigned. Viral suppression at this level requires intensive clinical review, adherence protocol overhaul, and PDSA rapid-cycle improvement.' };
    if (issueType === 'Viral Suppression — Not Achieving Target' && med)
        return { dtLevel: 3, route: 'ccta-support',     recommendation: 'CCTA specialist will conduct a systematic adherence review and co-develop a viral suppression improvement plan.' };
    if (issueType === 'Viral Suppression — Not Achieving Target')
        return { dtLevel: 2, route: 'peer-to-peer',     recommendation: 'Connect with a peer site that recently improved viral suppression outcomes. Adherence tracking tools available in the resource library.' };
    if (issueType === 'PrEP Initiation / Uptake' && high)
        return { dtLevel: 3, route: 'ccta-support',     recommendation: 'CCTA specialist engaged for PrEP initiation protocol review, provider training assessment, and patient education strategy.' };
    if (issueType === 'PrEP Initiation / Uptake')
        return { dtLevel: 2, route: 'peer-to-peer',     recommendation: 'Peer connection with a site running a strong PrEP uptake program. Shared patient education and provider training materials.' };
    if (issueType === 'Medication Adherence' && high)
        return { dtLevel: 4, route: 'community-expert', recommendation: 'Community Expert assigned. Severe adherence issues require TXTXT or TMI implementation review, intensive patient engagement protocol, and potential SBIRT screening integration.' };
    if (issueType === 'Medication Adherence' && med)
        return { dtLevel: 3, route: 'ccta-support',     recommendation: 'CCTA consultation for adherence support strategy — including motivational interviewing training (TMI), text-based reminders (TXTXT), and peer navigation.' };
    if (issueType === 'Medication Adherence')
        return { dtLevel: 2, route: 'peer-to-peer',     recommendation: 'Peer learning session on adherence support tools. Self-service resources include TXTXT and TMI implementation guides.' };
    if (issueType === 'Linkage to Care After Diagnosis' && high)
        return { dtLevel: 4, route: 'community-expert', recommendation: 'Community Expert assigned. Rapid linkage failure at this severity requires immediate protocol review — Project CONNECT implementation is strongly recommended.' };
    if (issueType === 'Linkage to Care After Diagnosis')
        return { dtLevel: 3, route: 'ccta-support',     recommendation: 'CCTA consultation using Project CONNECT framework — early orientation, relationship building, and linkage coordinator training.' };
    if (issueType === 'Care Navigation / Coordination' && high)
        return { dtLevel: 3, route: 'ccta-support',     recommendation: 'CCTA specialist engaged for navigation workflow redesign and care coordination protocol review.' };
    if (issueType === 'Care Navigation / Coordination')
        return { dtLevel: 2, route: 'peer-to-peer',     recommendation: 'Peer connection with a site running strong navigation workflows. Shared care coordination templates available.' };
    if (issueType === 'Digital Intervention — Technology Issue' && high)
        return { dtLevel: 3, route: 'ccta-support',     recommendation: 'CCTA consultation to assess the technology barrier — covering TXTXT troubleshooting, patient re-engagement, and backup adherence support protocols.' };
    if (issueType === 'Digital Intervention — Technology Issue')
        return { dtLevel: 2, route: 'peer-to-peer',     recommendation: 'Peer connection with a site running the same digital intervention. Shared troubleshooting guides and patient communication templates.' };
    if ((issueType === 'Staffing / Workforce' || issueType === 'Workforce') && high)
        return { dtLevel: 3, route: 'ccta-support',     recommendation: 'CCTA specialist engaged for workforce stabilization and cross-training protocol.' };
    if ((issueType === 'Staffing / Workforce' || issueType === 'Workforce') && med)
        return { dtLevel: 2, route: 'peer-to-peer',     recommendation: 'Connect with a peer site that resolved similar workforce challenges. Shared training templates available.' };
    if ((issueType === 'Data Quality & Reporting' || issueType === 'Data & Reporting') && high)
        return { dtLevel: 3, route: 'ccta-support',     recommendation: 'CCTA specialist will review data systems and establish a recovery timeline within 48 hours.' };
    if ((issueType === 'Data Quality & Reporting' || issueType === 'Data & Reporting') && med)
        return { dtLevel: 2, route: 'peer-to-peer',     recommendation: 'Connect with a peer site that resolved similar EHR workflow challenges.' };
    if (issueType === 'Community Outreach & Engagement' && high)
        return { dtLevel: 4, route: 'community-expert', recommendation: 'Community Expert assigned — outreach gaps at this severity require intensive community engagement strategy and cultural tailoring review.' };
    if (issueType === 'Community Outreach & Engagement' && med)
        return { dtLevel: 3, route: 'ccta-support',     recommendation: 'CCTA consultation for community engagement protocol review and outreach strategy design.' };
    if (issueType === 'Partner / Referral Coordination' && high)
        return { dtLevel: 3, route: 'ccta-support',     recommendation: 'CCTA specialist will map your referral network gaps and co-develop a coordination protocol with partner agencies.' };
    if (issueType === 'Clinic Workflow / Operations')
        return { dtLevel: 2, route: 'peer-to-peer',     recommendation: 'Peer connection with a site that optimized clinical workflows. Shared efficiency tools and patient flow templates.' };
    if (med)
        return { dtLevel: 2, route: 'peer-to-peer',     recommendation: 'Peer learning session recommended. Review shared resource library for quick wins.' };
    if (urgency === 'LOW' || issueType === 'Other')
        return { dtLevel: 1, route: 'self-service',     recommendation: 'Review the TERRA self-service resource library. Knowledge base articles matched to your issue type.' };
    return { dtLevel: 2, route: 'peer-to-peer',         recommendation: 'Peer connection scheduled within 48 hours.' };
}
